import { Component, ElementRef, Inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatPaginator, MatSort } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { combineLatest } from 'rxjs/observable/combineLatest';
import { debounceTime } from 'rxjs/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/operators/distinctUntilChanged';
import { first } from 'rxjs/operators/first';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { fromEvent } from 'rxjs/observable/fromEvent';

import { DataTableSource } from '../../../classes/DataTableSource/datatablesource';

import { ApplicationService, StudentSearchResult } from '../../application/application.service';
import { ReportInfo } from '../../reports/reports.service';
import { BaseReportRunOptions } from '../reportoptions/reportoptions.dialogcomponent';
import { SplashService } from '../../splash/splash.service';
import { UserInfo, UserService } from '../../user/user.service';

@Component({
    selector: 'dialog-studentsearch-component',
    styleUrls: ['studentsearch.dialogcomponent.css'],
    templateUrl: 'studentsearch.dialogcomponent.html',
})
export class StudentSearchDialogComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;
    @ViewChild('filter') filter: ElementRef;

    private _subrelease: Subject<void> = new Subject<void>();

    AllHeaders: string[] = ['LastName', 'FirstName', 'StudentID', 'School', 'Grade', 'Actions'];
    AvailableSchoolGrades: string[];
    canceltext: string;
    CurrentSchoolYear: string;
    forcedLocationNumber: string;
    LastSizeCode: string;
    message: string;
    oktext: string;
    ReportInfo: ReportInfo;
    SearchForm: FormGroup;
    SearchReady: boolean;
    ShowSearch: boolean;
    SmallDisplay: boolean;
    title: string;
    User: UserInfo;
    StudentList: StudentSearchResult[];
    StudentSearchConducted: boolean;
    StudentData: DataTableSource<StudentSearchResult>;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<StudentSearchDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: BaseStudentSearchDialogOptions,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private userService: UserService
    ) {
        this.canceltext = data.CancelText || 'Cancel';
        this.message = data.MessageText || '';
        this.oktext = data.OKText || 'Select';
        this.title = data.TitleText || 'User Search';
        this.ReportInfo = data.ReportInfo;
        this.AvailableSchoolGrades = data.AvailableSchoolGrades || ['All', '06', '07', '08', '09', '10', '11', '12'];
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;
        self.SearchReady = false;
        self.ShowSearch = true;

        self.LastSizeCode = self.mediaChange.isActive('xs')
            ? 'xs'
            : self.mediaChange.isActive('sm')
            ? 'sm'
            : self.mediaChange.isActive('md')
            ? 'md'
            : self.mediaChange.isActive('lg')
            ? 'lg'
            : 'xl';
        self.SmallDisplay = self.LastSizeCode === 'xs' || self.LastSizeCode === 'sm';

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.LastSizeCode = media.mqAlias;
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns =
                    !self.StudentList || !self.StudentList.length
                        ? ['NoData']
                        : self.SmallDisplay
                        ? ['SmallDisplay']
                        : self.VisibleHeaders.slice();
            });

        fromEvent(self.filter.nativeElement, 'keyup')
            .pipe(
                takeUntil(self._subrelease),
                debounceTime(150),
                distinctUntilChanged()
            )
            .subscribe(() => {
                if (!self.StudentData) {
                    return;
                }
                self.StudentData.filter = this.filter.nativeElement.value;
            });

        // Wait for first non-null user object to load component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(item => item && !!item.AuthToken)
        ).subscribe(user => {
            self.User = user;
            self.SearchReady = true;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            // Load system values to continue.
            const curYearObs = self.appService.GetCurrentSchoolYear();

            combineLatest(curYearObs, function(curYear) {
                return {
                    CurrentYear: curYear,
                };
            })
                .pipe(takeUntil(self._subrelease))
                .subscribe(packet => {
                    self.CurrentSchoolYear = packet.CurrentYear;

                    // Prepare the rest of the component
                    this.SearchForm = this.fb.group(
                        {
                            FirstName: [''],
                            LastName: [''],
                            StudentID: [''],
                            Grade: [''],
                        },
                        {
                            validator: function(ctrl: FormGroup) {
                                if (!ctrl) {
                                    return null;
                                }

                                const form = ctrl.value;

                                if (!form.FirstName && !form.LastName && !form.StudentID && !form.Grade) {
                                    return { noneprovided: true };
                                }
                                if (!form.StudentID && !form.Grade) {
                                    // Check to make sure we have at least 5 characters combined from the firstname and lastname fields
                                    const fn = (form.FirstName || '').length;
                                    const ln = (form.LastName || '').length;

                                    if (fn + ln < 5) {
                                        return { TooFewCharacters: true };
                                    }
                                }

                                return null;
                            },
                        }
                    );

                    this.StudentList = null;
                    this.StudentData = new DataTableSource<StudentSearchResult>(
                        [],
                        self.paginator,
                        self.sorter,
                        self.SortStudents.bind(self),
                        ['LastName', 'FirstName', 'StudentID', 'SchoolName']
                    );
                    this.StudentSearchConducted = false;
                });
        });
    }

    // #region Cancel Handler Method

    CancelHandler() {
        // User cancelled.
        this.dialogRef.close(null);
    }

    // #endregion

    // #region Handle User Key Method

    HandleUserKey(key: any) {
        this.HandleStudentSearch();
    }

    // #endregion

    // #region Handle Student Search Method

    HandleStudentSearch() {
        const self = this;

        const form = self.SearchForm.value;

        if (!form.FirstName && !form.LastName && !form.StudentID && !form.Grade) {
            self.splashService.SnackbarError('You must provide at least 1 search parameter in order to search.');
            return;
        }

        if (self.SearchForm.invalid) {
            return;
        }

        // Call the search for the user
        self.appService
            .SearchForStudent(
                self.User.AuthToken,
                self.User.CurrentRole,
                self.CurrentSchoolYear,
                null,
                form.StudentID,
                null,
                null,
                self.CurrentSchoolYear,
                form.Grade,
                form.FirstName,
                form.LastName
            )
            .subscribe(
                students => {
                    // If small display format, hide the search form.
                    if (self.SmallDisplay) {
                        self.ShowSearch = false;
                    }

                    // Update into table with search results
                    self.StudentList = students;

                    if (students && students.length) {
                        self.StudentData.Update(students);
                        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                        self.VisibleColumns = self.SmallDisplay ? ['SmallDisplay'] : self.VisibleHeaders.slice();
                    } else {
                        self.StudentData.Update([{}]);
                        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                        self.VisibleColumns = ['NoData'];
                    }

                    self.StudentSearchConducted = true;
                },
                error => {
                    let msg: string = null;

                    if (error instanceof Response) {
                        const body = <any>error.json();
                        msg = body.Message || (typeof body === 'string' ? body : 'An unknown error has occurred.');
                    } else {
                        let errorPacket = error.message || error;
                        if (errorPacket != null && errorPacket !== '') {
                            errorPacket = JSON.parse(errorPacket);
                        }
                        msg =
                            errorPacket != null && errorPacket instanceof Object
                                ? errorPacket.ExceptionMessage || errorPacket.Message || error
                                : errorPacket || 'An unknown error has occurred.';
                    }

                    if (msg) {
                        self.splashService.SnackbarError(msg);
                    }
                }
            );
    }

    // #endregion

    // #region OK Handler Method

    OKHandler(student: StudentSearchResult) {
        // User selected a search result. Pass back.
        this.dialogRef.close(<BaseReportRunOptions>{
            ReportInfo: this.ReportInfo,
            StudentID: student && student.StudentID,
        });
    }

    // #endregion

    // #region Show Search Form Method

    ShowSearchForm() {
        this.ShowSearch = true;
        this.StudentSearchConducted = false;
    }

    // #endregion

    // #region Sort Students Method

    SortStudents(First: StudentSearchResult, Second: StudentSearchResult) {
        const self = this;

        let PropA: string | number;
        let PropB: string | number;

        switch (self.sorter.active) {
            case 'LastName':
                [PropA, PropB] = [First.LastName, Second.LastName];
                break;
            case 'FirstName':
                [PropA, PropB] = [First.FirstName, Second.FirstName];
                break;
            case 'StudentID':
                [PropA, PropB] = [First.StudentID, Second.StudentID];
                break;
            case 'School':
                [PropA, PropB] = [First.SchoolName, Second.SchoolName];
                break;
            case 'Grade':
                [PropA, PropB] = [First.GradeLevel, Second.GradeLevel];
                break;
        }

        const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
        const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

        return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
    }

    // #endregion
}

// #region Custom Classes

// #region Student Search Dialog Options Class

export class BaseStudentSearchDialogOptions {
    TitleText?: string;
    MessageText?: string;
    OKText?: string;
    CancelText?: string;
    IconColor?: string;
    IconName?: string;
    ShowIcon?: boolean;
    height?: string;
    width?: string;
    AvailableSchoolGrades?: string[];
    ReportInfo?: ReportInfo;
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/services/dialogs/studentsearch/studentsearch.dialogcomponent.ts